import type { Cell, CellContext, Row } from '@tanstack/react-table';
import * as React from 'react';
import type { ArrayPath, FieldArrayWithId, FieldValues } from 'react-hook-form';

import { withEditingTableCellNameContext } from 'af-root/context/editingTableCellNameContext';
import { useEditingTableCellName } from 'af-root/hooks/useEditingTableCellName';

import type { FooterButton } from 'af-components/Controls/SimpleTable/types';
import type { ColumnDef, FooterRowDef } from 'af-components/RHFTableCellComponents/SimpleTable/types';

import DeleteCell from './DeleteCell';
import styles from './styles.module.scss';
import { SimpleTable, type TableRef } from '../../RHFTableCellComponents/SimpleTable';

type SharedProps
	<
		T,
		TFieldValues extends FieldValues,
		TFieldArrayName extends ArrayPath<TFieldValues>
	> = {
		fields: FieldArrayWithId<TFieldValues, TFieldArrayName>[];
		footerButtonsLeft?: FooterButton[];
		footerButtonsRight?: FooterButton[];
		headerButtons?: FooterButton[];
		rowClassName?: string | ((row: Row<T>) => string);
		label?: string;
		labelTooltip?: string;
		emptyTableMessage?: string;
		onFieldsUpdated?: (arrayItems: T[]) => void;
		columns: ColumnDef<T>[];
		onRemove?: (index?: number | number[]) => void;
		remove?: (index?: number | number[]) => void;
		formAccessorName: string;
		/** If false, delete column is not added at all (default: true) */
		includeDeleteColumn?: boolean;
		/** If provided, delete button is only rendered when this returns true for the row */
		canDeleteRow?: (row: T & SimpleTableRow) => boolean;
		className?: string;
		readonly?: boolean;
		getRowId?: (row: T) => string;
		getCellClassName?: (cell: Cell<T, unknown>) => void;
		height?: React.CSSProperties['height'];
		sideDrawer?: () => React.JSX.Element;
		drawerTitle?: string;
		footerRow?: FooterRowDef;
		iconName?: string;
		onRowClicked?: (row: Row<T>) => void;
		/** If true, renders rows virtually for large datasets */
		virtualizeRows?: boolean;
		titleClassName?: string;
	};

export type SimpleTableRow = {
	/** Index inside rhf fields array */
	index: number;

};

/**
 * Wrapper around SimpleTable
 * Uses 'editingCellName' from context, which is updated when cell is clicked
 * Based on that, new controlled RHF component is rendered in that cell
 * Only 'Delete' action cell exists, no more 'Edit' because all cells are editable by default
 * @param props
 * @returns
 */
function SimpleTableRHFWrapperInner<T, TFieldValues extends FieldValues, TFieldArrayName extends ArrayPath<TFieldValues>>(
	props: SharedProps<
		T,
		TFieldValues,
		TFieldArrayName
	>,
	ref: React.Ref<TableRef>
) {
	const {
		className,
		columns,
		fields,
		footerButtonsLeft,
		label,
		labelTooltip,
		emptyTableMessage,
		footerButtonsRight,
		headerButtons,
		remove,
		onRemove,
		formAccessorName,
		includeDeleteColumn = true,
		canDeleteRow,
		readonly,
		getRowId,
		getCellClassName,
		height,
		sideDrawer,
		drawerTitle,
		footerRow,
		iconName,
		onRowClicked,
		titleClassName,
	} = props;

	const editingCellName = useEditingTableCellName();

	const rows = React.useMemo(() => {
		return fields.map((fieldRow, index) => {
			return ({ ...fieldRow, index } as T & SimpleTableRow);
		});
	}, [fields]);

	const onDeleteClick = React.useCallback((_cell: CellContext<T & SimpleTableRow, unknown>) => () => {
		remove?.(_cell.row.original.index);
		if (onRemove) {
			onRemove(_cell.row.original.index);
		}
	}, [onRemove, remove]);

	const simpleTableFieldColumns = React.useMemo((): ColumnDef<T>[] => {
		const columnsWithActions = [...columns];

		// If delete column is explicitly disabled, just return the base columns.
		if (!includeDeleteColumn) {
			return columnsWithActions;
		}

		if (!remove) {
			return columnsWithActions;
		}
		return [
			...columnsWithActions,
			{
				id: 'delete',
				isDisplayColumn: true,
				header: null,
				cell: (_cell: CellContext<T & SimpleTableRow, unknown>) => {
					const row = _cell.row.original as T & SimpleTableRow;

					const isArchived = 'deletedAt' in row && row.deletedAt;

					if (canDeleteRow && !canDeleteRow(row)) {
						return <div className={isArchived ? styles.archived : ''} />;
					}

					return (
						<div className={isArchived ? styles.archived : styles.nonArchived}>
							<DeleteCell icon={iconName} onDeleteClick={onDeleteClick(_cell)} />
						</div>
					);
				},
				size: 32,
			} as ColumnDef<T>,
		];
	}, [columns, includeDeleteColumn, canDeleteRow, onDeleteClick, remove, iconName]);

	const onCellClicked = React.useCallback((cellName: string) => {
		if (!readonly && cellName && editingCellName && editingCellName.editingTableCellName !== cellName) {
			editingCellName?.setEditingTableCellName(cellName);
		}
	}, [editingCellName, readonly]);

	return (
		<SimpleTable<T>
			arrayAccessorName={formAccessorName}
			className={className}
			columns={simpleTableFieldColumns}
			disableAllSortings={false}
			drawerTitle={drawerTitle}
			emptyTableMessage={emptyTableMessage}
			footerButtonsLeft={footerButtonsLeft}
			footerButtonsRight={footerButtonsRight}
			footerRow={footerRow}
			getCellClassName={getCellClassName}
			getRowId={getRowId}
			headerButtons={headerButtons}
			height={height}
			label={label}
			labelTooltip={labelTooltip}
			onCellClicked={onCellClicked}
			onRowClicked={onRowClicked}
			readonly={readonly}
			ref={ref}
			rows={rows ?? []}
			sideDrawer={sideDrawer}
			titleClassName={titleClassName}
			virtualizeRows={props.virtualizeRows}
		/>
	);
}

const SimpleTableRHFWrapper = React.forwardRef(SimpleTableRHFWrapperInner) as <
	T,
	TFieldValues extends FieldValues,
	TFieldArrayName extends ArrayPath<TFieldValues>
>(props: SharedProps<T, TFieldValues, TFieldArrayName> & { ref?: React.Ref<TableRef>; }) => React.JSX.Element;

export default withEditingTableCellNameContext(SimpleTableRHFWrapper) as typeof SimpleTableRHFWrapper;
